import React, { useState } from "react";
import { IDog, IDogRun } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";

import "./UKCRallyRunForm.scss";

interface IProps {
  dog: IDog;
  onSubmit: (formData: any) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
  editRun?: IDogRun | null;
}

const UKCRallyRunForm: React.FC<IProps> = ({ dog, onSubmit, onCancel, isSubmitting, editRun }) => {
  const [rallyClass, setRallyClass] = useState(editRun?.class || "");
  const [score, setScore] = useState(editRun?.score?.toString() || "");
  const [resultStatus, setResultStatus] = useState(editRun?.result_status || Constants.run_result_status.qualified);
  const [trialDate, setTrialDate] = useState(
    editRun?.trial_date ? new Date(editRun.trial_date).toISOString().split('T')[0] : ""
  );
  const [placement, setPlacement] = useState(editRun?.placement?.toString() || "");
  const [dogsDefeated, setDogsDefeated] = useState(editRun?.dogs_defeated?.toString() || "");
  const [awards, setAwards] = useState<string[]>(editRun?.awards || []);
  const [judgeNumber, setJudgeNumber] = useState(editRun?.judge_number || "");
  const [judgeName, setJudgeName] = useState(editRun?.judge_name || "");
  const [eventName, setEventName] = useState(editRun?.event_name || "");
  const [city, setCity] = useState(editRun?.location?.city || "");
  const [state, setState] = useState(editRun?.location?.state || "");
  const [notes, setNotes] = useState(editRun?.notes || "");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Parse the date and set it to noon local time to avoid timezone issues
    const [year, month, day] = trialDate.split('-').map(Number);
    const localDate = new Date(year, month - 1, day, 12, 0, 0);
    
    const formData: any = {
      dogId: dog._id,
      rallyClass,
      score: parseFloat(score),
      resultStatus,
      trialDate: localDate,
      judgeNumber: judgeNumber || undefined,
      judgeName: judgeName || undefined,
      placement: placement ? parseInt(placement) : undefined,
      dogsDefeated: dogsDefeated ? parseInt(dogsDefeated) : undefined,
      awards: awards.length > 0 ? awards : undefined,
      eventName: eventName || undefined,
      location: (city || state) ? { city, state } : undefined,
      notes: notes || undefined
    };

    if (editRun) {
      formData.run_id = editRun._id;
    }

    await onSubmit(formData);
  };

  const handleAwardToggle = (award: string) => {
    if (awards.includes(award)) {
      setAwards(awards.filter(a => a !== award));
    } else {
      setAwards([...awards, award]);
    }
  };

  return (
    <form className="ukcRallyRunForm" onSubmit={handleSubmit}>
      <h2>{editRun ? 'Edit' : 'Add'} UKC Rally Run</h2>
      <p className="dogName">For: {dog.call_name}</p>

      <div className="formGrid">
        {/* Class */}
        <div className="formGroup">
          <label htmlFor="rallyClass">Class *</label>
          <select
            id="rallyClass"
            value={rallyClass}
            onChange={(e) => setRallyClass(e.target.value)}
            required
          >
            <option value="">Select class...</option>
            <option value={Constants.ukc_rally_class.level_1_a}>Level 1 A</option>
            <option value={Constants.ukc_rally_class.level_1_b}>Level 1 B</option>
            <option value={Constants.ukc_rally_class.level_2_a}>Level 2 A</option>
            <option value={Constants.ukc_rally_class.level_2_b}>Level 2 B</option>
            <option value={Constants.ukc_rally_class.level_3_a}>Level 3 A</option>
            <option value={Constants.ukc_rally_class.level_3_b}>Level 3 B</option>
            <option value={Constants.ukc_rally_class.masters_a}>Masters A</option>
            <option value={Constants.ukc_rally_class.masters_b}>Masters B</option>
          </select>
        </div>

        {/* Score */}
        <div className="formGroup">
          <label htmlFor="score">Score * (0-200)</label>
          <input
            id="score"
            type="number"
            min="0"
            max="200"
            step="0.5"
            value={score}
            onChange={(e) => setScore(e.target.value)}
            required
          />
          <span className="fieldHelp">Half points allowed (e.g., 185.5)</span>
        </div>

        {/* Result Status */}
        <div className="formGroup">
          <label htmlFor="resultStatus">Result *</label>
          <select
            id="resultStatus"
            value={resultStatus}
            onChange={(e) => setResultStatus(e.target.value)}
            required
          >
            <option value={Constants.run_result_status.qualified}>Qualified</option>
            <option value={Constants.run_result_status.not_qualified}>Not Qualified</option>
          </select>
        </div>

        {/* Trial Date */}
        <div className="formGroup">
          <label htmlFor="trialDate">Trial Date *</label>
          <input
            id="trialDate"
            type="date"
            value={trialDate}
            onChange={(e) => setTrialDate(e.target.value)}
            required
          />
        </div>

        {/* Placement */}
        <div className="formGroup">
          <label htmlFor="placement">Placement</label>
          <input
            id="placement"
            type="number"
            min="1"
            value={placement}
            onChange={(e) => setPlacement(e.target.value)}
            placeholder="1st, 2nd, etc."
          />
        </div>

        {/* Dogs Defeated */}
        <div className="formGroup">
          <label htmlFor="dogsDefeated">Dogs Defeated</label>
          <input
            id="dogsDefeated"
            type="number"
            min="0"
            value={dogsDefeated}
            onChange={(e) => setDogsDefeated(e.target.value)}
            placeholder="For championship points"
          />
        </div>
      </div>

      {/* Awards Section */}
      <div className="formSection">
        <h3>Awards (for Championship Points)</h3>
        <div className="checkboxGroup">
          <label>
            <input
              type="checkbox"
              checked={awards.includes(Constants.ukc_obedience_award.high_in_trial)}
              onChange={() => handleAwardToggle(Constants.ukc_obedience_award.high_in_trial)}
            />
            <span>High in Trial</span>
          </label>
        </div>
      </div>

      {/* Judge Information */}
      <div className="formSection">
        <h3>Judge Information</h3>
        <div className="formGrid">
          <div className="formGroup">
            <label htmlFor="judgeNumber">Judge Number</label>
            <input
              id="judgeNumber"
              type="text"
              value={judgeNumber}
              onChange={(e) => setJudgeNumber(e.target.value)}
            />
          </div>
          <div className="formGroup">
            <label htmlFor="judgeName">Judge Name</label>
            <input
              id="judgeName"
              type="text"
              value={judgeName}
              onChange={(e) => setJudgeName(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Event Information */}
      <div className="formSection">
        <h3>Event Information</h3>
        <div className="formGroup">
          <label htmlFor="eventName">Event Name</label>
          <input
            id="eventName"
            type="text"
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
          />
        </div>
        <div className="formGrid">
          <div className="formGroup">
            <label htmlFor="city">City</label>
            <input
              id="city"
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
            />
          </div>
          <div className="formGroup">
            <label htmlFor="state">State</label>
            <input
              id="state"
              type="text"
              value={state}
              onChange={(e) => setState(e.target.value)}
              maxLength={2}
              placeholder="CA"
            />
          </div>
        </div>
      </div>

      {/* Notes */}
      <div className="formSection">
        <div className="formGroup">
          <label htmlFor="notes">Notes</label>
          <textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
          />
        </div>
      </div>

      <div className="formActions">
        <button type="button" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </button>
        <button type="submit" disabled={isSubmitting} className="submitButton">
          {isSubmitting ? 'Saving...' : (editRun ? 'Update Run' : 'Add Run')}
        </button>
      </div>
    </form>
  );
};

export default UKCRallyRunForm;

